import { useCallback, useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { toast } from "sonner";
import { MessageSquareText, Pencil, Trash2, Users, User } from "lucide-react";

const ReplyForm = ({ initial, isAdmin, onDone, onCancel }) => {
  const [r, setR] = useState(initial);
  const save = () => (r.id ? api.put(`/staff/mail-replies/${r.id}`, r) : api.post("/staff/mail-replies", r))
    .then(() => { toast.success("Saved reply stored"); onDone(); }).catch((e) => toast.error(errText(e)));
  return (
    <div className="space-y-2 border border-slate-200 rounded-xl p-3" data-testid="reply-form">
      <Input value={r.title} onChange={(e) => setR({ ...r, title: e.target.value })} maxLength={80} placeholder="Name (e.g. Payment received)" data-testid="reply-form-title" />
      <Textarea value={r.body} onChange={(e) => setR({ ...r, body: e.target.value })} rows={6} placeholder="Reply text…" data-testid="reply-form-body" />
      <p className="text-xs text-slate-400">Type <code className="bg-slate-100 px-1 rounded">{"{first_name}"}</code> to fill in the customer's first name. If it's unknown, a highlighted blank is left for you to fill in.</p>
      {isAdmin && <label className="flex items-center justify-between text-sm">Shared with all staff <Switch checked={r.shared} onCheckedChange={(v) => setR({ ...r, shared: v })} data-testid="reply-form-shared" /></label>}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="reply-form-cancel">Cancel</Button>
        <Button size="sm" onClick={save} className="bg-[#F5551E] hover:bg-[#e04a15]" data-testid="reply-form-save">Save</Button>
      </div>
    </div>
  );
};

const ManageReplies = ({ replies, reload, onClose }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [edit, setEdit] = useState(null);
  const canEdit = (r) => (r.shared ? isAdmin : true);
  const del = (r) => window.confirm(`Delete "${r.title}"?`) && api.delete(`/staff/mail-replies/${r.id}`).then(() => { toast.success("Saved reply deleted"); reload(); }).catch((e) => toast.error(errText(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="manage-replies-dialog">
        <DialogHeader><DialogTitle>Saved replies</DialogTitle></DialogHeader>
        {edit ? <ReplyForm initial={edit} isAdmin={isAdmin} onCancel={() => setEdit(null)} onDone={() => { setEdit(null); reload(); }} /> : (
          <div className="space-y-3">
            <ul className="divide-y divide-slate-100 max-h-80 overflow-auto" data-testid="manage-replies-list">
              {replies.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0">{r.shared ? <Users size={14} className="text-[#F5551E] shrink-0" /> : <User size={14} className="text-slate-400 shrink-0" />}<span className="truncate">{r.title}</span></span>
                  {canEdit(r) && <span className="flex shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => setEdit(r)} data-testid={`reply-edit-${r.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r)} className="text-red-600" data-testid={`reply-delete-${r.id}`}><Trash2 size={14} /></Button>
                  </span>}
                </li>
              ))}
              {!replies.length && <li className="py-3 text-sm text-slate-400">No saved replies yet.</li>}
            </ul>
            <p className="text-xs text-slate-400"><Users size={12} className="inline text-[#F5551E]" /> shared with all staff (admins manage) · <User size={12} className="inline" /> only you</p>
            <Button onClick={() => setEdit({ title: "", body: "", shared: false })} data-testid="reply-new" className="w-full bg-[#12234A]">New saved reply</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SavedReplies = ({ onInsert }) => {
  const [replies, setReplies] = useState([]);
  const [manage, setManage] = useState(false);
  const reload = useCallback(() => api.get("/staff/mail-replies").then((r) => setReplies(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  const groups = [["Shared", replies.filter((r) => r.shared)], ["Mine", replies.filter((r) => !r.shared)]];
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="saved-replies-btn"><MessageSquareText size={15} /> Saved replies</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-auto">
          {groups.map(([label, list]) => list.length > 0 && (
            <div key={label}>
              <DropdownMenuLabel className="text-[11px] uppercase text-slate-400">{label}</DropdownMenuLabel>
              {list.map((r) => <DropdownMenuItem key={r.id} onClick={() => onInsert(r.body)} data-testid={`saved-reply-${r.id}`}>{r.title}</DropdownMenuItem>)}
            </div>
          ))}
          {!replies.length && <DropdownMenuLabel className="text-sm font-normal text-slate-400">No saved replies yet</DropdownMenuLabel>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setManage(true)} data-testid="saved-replies-manage">Manage saved replies…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {manage && <ManageReplies replies={replies} reload={reload} onClose={() => setManage(false)} />}
    </>
  );
};
